/**
 * 🚀 Performance Manager - Sistema de Optimización MGI
 * DÍA 4 PASO 4.3: Optimización de Performance
 * 
 * Características:
 * - Cache inteligente con TTL
 * - Debouncing de requests
 * - Preloading de datos críticos
 * - Monitoreo de performance
 * - Optimización de queries
 */

class PerformanceManager {
  constructor() {
    this.cache = new Map()
    this.requestQueue = new Map()
    this.performanceMetrics = {
      cacheHits: 0,
      cacheMisses: 0,
      avgResponseTime: 0,
      totalRequests: 0,
      errorRate: 0
    }
    this.observers = []
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutos
      maxCacheSize: 100,
      debounceTime: 300,
      preloadEnabled: true,
      monitoringEnabled: true
    }
    
    this.initializeMonitoring()
  }

  /**
   * Cache inteligente con TTL y LRU
   */
  setCache(key, data, ttl = this.config.defaultTTL) {
    const item = {
      data,
      timestamp: Date.now(),
      ttl,
      hits: 0,
      lastAccessed: Date.now()
    }

    // LRU: Si el cache está lleno, eliminar el menos usado
    if (this.cache.size >= this.config.maxCacheSize) {
      const oldestKey = this.findLRUKey()
      this.cache.delete(oldestKey)
    }

    this.cache.set(key, item)
    this.notifyObservers('cache:set', { key, size: this.cache.size })
  }

  getCache(key) {
    const item = this.cache.get(key)
    
    if (!item) {
      this.performanceMetrics.cacheMisses++
      return null
    }

    // Verificar TTL
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key)
      this.performanceMetrics.cacheMisses++
      return null
    }

    // Actualizar estadísticas
    item.hits++
    item.lastAccessed = Date.now()
    this.performanceMetrics.cacheHits++
    
    this.notifyObservers('cache:hit', { key, hits: item.hits })
    return item.data
  }

  findLRUKey() {
    let oldestKey = null
    let oldestTime = Date.now()

    for (const [key, item] of this.cache.entries()) {
      if (item.lastAccessed < oldestTime) {
        oldestTime = item.lastAccessed
        oldestKey = key
      }
    }

    return oldestKey
  }

  /**
   * Debouncing para evitar requests duplicados
   */
  debounceRequest(key, requestFn, delay = this.config.debounceTime) {
    return new Promise((resolve, reject) => {
      // Cancelar request anterior si existe
      if (this.requestQueue.has(key)) {
        clearTimeout(this.requestQueue.get(key).timeout)
      }

      const timeout = setTimeout(async () => {
        try {
          const startTime = performance.now()
          const result = await requestFn()
          const endTime = performance.now()
          
          this.updatePerformanceMetrics(endTime - startTime, true)
          this.requestQueue.delete(key)
          resolve(result)
        } catch (error) {
          this.updatePerformanceMetrics(0, false)
          this.requestQueue.delete(key)
          reject(error)
        }
      }, delay)

      this.requestQueue.set(key, { timeout, resolve, reject })
    })
  }

  /**
   * Request optimizado con cache y debouncing
   */
  async optimizedRequest(url, options = {}) {
    const cacheKey = this.generateCacheKey(url, options)
    
    // 1. Intentar obtener del cache
    const cachedData = this.getCache(cacheKey)
    if (cachedData && !options.forceRefresh) {
      return cachedData
    }

    // 2. Request con debouncing
    const requestFn = async () => {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      })

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`)
      }

      const data = await response.json()
      
      // 3. Guardar en cache
      this.setCache(cacheKey, data, options.ttl)
      
      return data
    }

    return this.debounceRequest(cacheKey, requestFn)
  }

  generateCacheKey(url, options) {
    const params = options.params ? JSON.stringify(options.params) : ''
    const method = options.method || 'GET'
    return `${method}:${url}:${params}`
  }

  /**
   * Preloading de datos críticos
   */
  async preloadCriticalData(endpoints = []) {
    if (!this.config.preloadEnabled) return

    const preloadPromises = endpoints.map(async (endpoint) => {
      try {
        const { url, options = {} } = endpoint
        const cacheKey = this.generateCacheKey(url, options)
        
        // Solo precargar si no está en cache
        if (!this.getCache(cacheKey)) {
          await this.optimizedRequest(url, { ...options, priority: 'low' })
          this.notifyObservers('preload:success', { url })
        }
      } catch (error) {
        this.notifyObservers('preload:error', { url: endpoint.url, error })
      }
    })

    await Promise.allSettled(preloadPromises)
  }

  /**
   * Optimización de queries por lotes
   */
  async batchRequests(requests = []) {
    const batchKey = `batch_${Date.now()}`
    const startTime = performance.now()

    try {
      // Agrupar requests por dominio/endpoint base
      const groupedRequests = this.groupRequestsByEndpoint(requests)
      
      // Ejecutar requests en paralelo por grupo
      const results = await Promise.allSettled(
        Object.entries(groupedRequests).map(async ([endpoint, reqs]) => {
          return Promise.all(
            reqs.map(req => this.optimizedRequest(req.url, req.options))
          )
        })
      )

      const endTime = performance.now()
      this.updatePerformanceMetrics(endTime - startTime, true)
      
      this.notifyObservers('batch:complete', { 
        batchKey, 
        requestCount: requests.length,
        duration: endTime - startTime 
      })

      return results.flat()
    } catch (error) {
      this.updatePerformanceMetrics(0, false)
      throw error
    }
  }

  groupRequestsByEndpoint(requests) {
    return requests.reduce((groups, request) => {
      const baseEndpoint = request.url.split('?')[0].split('/').slice(0, -1).join('/')
      if (!groups[baseEndpoint]) {
        groups[baseEndpoint] = []
      }
      groups[baseEndpoint].push(request)
      return groups
    }, {})
  }

  /**
   * Monitoreo de performance
   */
  initializeMonitoring() {
    if (!this.config.monitoringEnabled) return

    // Monitor cada 30 segundos
    setInterval(() => {
      this.collectPerformanceData()
    }, 30000)

    // Monitor de memoria del cache
    setInterval(() => {
      this.monitorCacheHealth()
    }, 60000)
  }

  collectPerformanceData() {
    const metrics = {
      ...this.performanceMetrics,
      cacheSize: this.cache.size,
      cacheHitRate: this.calculateCacheHitRate(),
      memoryUsage: this.estimateCacheMemoryUsage(),
      timestamp: Date.now()
    }

    this.notifyObservers('performance:metrics', metrics)
  }

  calculateCacheHitRate() {
    const total = this.performanceMetrics.cacheHits + this.performanceMetrics.cacheMisses
    return total > 0 ? (this.performanceMetrics.cacheHits / total * 100).toFixed(2) : 0
  }

  estimateCacheMemoryUsage() {
    let totalSize = 0
    for (const [key, item] of this.cache.entries()) {
      totalSize += JSON.stringify(item).length * 2 // Aproximación en bytes
    }
    return Math.round(totalSize / 1024) // KB
  }

  monitorCacheHealth() {
    // Limpiar entradas expiradas
    const now = Date.now()
    let cleaned = 0

    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key)
        cleaned++
      }
    }

    if (cleaned > 0) {
      this.notifyObservers('cache:cleaned', { removed: cleaned, remaining: this.cache.size })
    }
  }

  updatePerformanceMetrics(responseTime, success) {
    this.performanceMetrics.totalRequests++
    
    if (success) {
      // Calcular promedio móvil del tiempo de respuesta
      const currentAvg = this.performanceMetrics.avgResponseTime
      const totalRequests = this.performanceMetrics.totalRequests
      this.performanceMetrics.avgResponseTime = 
        (currentAvg * (totalRequests - 1) + responseTime) / totalRequests
    } else {
      this.performanceMetrics.errorRate = 
        (this.performanceMetrics.errorRate * (this.performanceMetrics.totalRequests - 1) + 1) / 
        this.performanceMetrics.totalRequests
    }
  }

  /**
   * Sistema de observadores para monitoreo
   */
  subscribe(callback) {
    this.observers.push(callback)
    return () => {
      this.observers = this.observers.filter(obs => obs !== callback)
    }
  }

  notifyObservers(event, data) {
    this.observers.forEach(callback => {
      try {
        callback(event, data)
      } catch (error) {
        console.error('Error en observer:', error)
      }
    })
  }

  /**
   * API pública para configuración
   */
  configure(newConfig) {
    this.config = { ...this.config, ...newConfig }
  }

  /**
   * Limpiar cache específico o todo
   */
  clearCache(pattern = null) {
    if (pattern) {
      const regex = new RegExp(pattern)
      for (const key of this.cache.keys()) {
        if (regex.test(key)) {
          this.cache.delete(key)
        }
      }
    } else {
      this.cache.clear()
    }
    this.notifyObservers('cache:cleared', { pattern })
  }

  /**
   * Estadísticas detalladas
   */
  getStats() {
    return {
      ...this.performanceMetrics,
      cacheSize: this.cache.size,
      cacheHitRate: this.calculateCacheHitRate(),
      memoryUsage: this.estimateCacheMemoryUsage(),
      activeRequests: this.requestQueue.size,
      config: this.config
    }
  }

  /**
   * Exportar métricas para análisis
   */
  exportMetrics() {
    const cacheEntries = Array.from(this.cache.entries()).map(([key, item]) => ({
      key,
      hits: item.hits,
      age: Date.now() - item.timestamp,
      ttl: item.ttl,
      size: JSON.stringify(item.data).length
    }))

    return {
      timestamp: Date.now(),
      performance: this.performanceMetrics,
      cache: {
        size: this.cache.size,
        hitRate: this.calculateCacheHitRate(),
        memoryUsage: this.estimateCacheMemoryUsage(),
        entries: cacheEntries
      },
      config: this.config
    }
  }
}

// Instancia singleton
export const performanceManager = new PerformanceManager()

// API de conveniencia
export const {
  optimizedRequest,
  preloadCriticalData,
  batchRequests,
  clearCache,
  getStats,
  configure,
  subscribe
} = performanceManager
